import React, { useEffect, useState } from "react";
import { useLang } from "../context/LanguageContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Check, Zap, Dumbbell, ChevronRight, Flame, Wind, Footprints, Sparkles,
  Move, Droplets, Shield, Activity, Moon,
} from "lucide-react";
import { toast } from "sonner";

const DAYS_ID = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];

const EX_CATEGORY = {
  warmup: "warmup", stretch: "stretch", gentle_walk: "walk", outdoor_walk: "walk",
  box_breathing: "breath", cat_cow: "yoga", child_pose: "yoga", seated_twist: "yoga",
  wall_pushup: "strength", chair_squat: "strength", sun_salute_slow: "yoga",
  hip_opener: "stretch", shoulder_roll: "mobility", foam_roll: "mobility", hydration: "hydration",
  squat: "strength", pushup: "strength", rows: "strength", plank: "core",
  jog_steady: "cardio", cooldown: "stretch", hollow_hold: "core", bird_dog: "core",
  side_plank: "core", pike_pushup: "strength", pullup: "strength", db_press: "strength",
  face_pull: "strength", hiit_intervals: "hiit", long_walk_bike: "cardio",
  yoga_flow_light: "yoga", rest_full: "rest",
};

const CAT_STYLE = {
  warmup:    { Icon: Flame,      bg: "bg-orange-100", color: "text-orange-700",    ring: "bg-orange-50" },
  stretch:   { Icon: Wind,       bg: "bg-yellow-100", color: "text-yellow-700",    ring: "bg-yellow-50" },
  walk:      { Icon: Footprints, bg: "bg-green-100",  color: "text-green-700",     ring: "bg-green-50" },
  breath:    { Icon: Wind,       bg: "bg-sky-100",    color: "text-sky-700",       ring: "bg-sky-50" },
  yoga:      { Icon: Sparkles,   bg: "bg-purple-100", color: "text-purple-700",    ring: "bg-purple-50" },
  strength:  { Icon: Dumbbell,   bg: "bg-[#EAF0E4]",  color: "text-[#4A5D23]",     ring: "bg-[#EAF0E4]" },
  mobility:  { Icon: Move,       bg: "bg-cyan-100",   color: "text-cyan-700",      ring: "bg-cyan-50" },
  hydration: { Icon: Droplets,   bg: "bg-blue-100",   color: "text-blue-700",      ring: "bg-blue-50" },
  core:      { Icon: Shield,     bg: "bg-indigo-100", color: "text-indigo-700",    ring: "bg-indigo-50" },
  cardio:    { Icon: Activity,   bg: "bg-rose-100",   color: "text-rose-700",      ring: "bg-rose-50" },
  hiit:      { Icon: Zap,        bg: "bg-red-100",    color: "text-red-700",       ring: "bg-red-50" },
  rest:      { Icon: Moon,       bg: "bg-slate-100",  color: "text-slate-700",     ring: "bg-slate-50" },
};

function categoryFor(item) {
  const c = EX_CATEGORY[item?.key];
  return CAT_STYLE[c] || CAT_STYLE.strength;
}

function todayISO() {
  return new Date().toISOString().split("T")[0];
}

export default function Workouts() {
  const { t, lang } = useLang();
  const [plan, setPlan] = useState(null);
  const [marks, setMarks] = useState({});
  const [loading, setLoading] = useState(false);
  const [openItem, setOpenItem] = useState(null);

  const load = async () => {
    const [{ data: p }, { data: m }] = await Promise.all([
      api.get("/workouts/plan"),
      api.get("/workouts/marks"),
    ]);
    setPlan(p && p.days ? p : null);
    const map = {};
    (m.marks || []).forEach((x) => { map[`${x.date}:${x.exercise_index}`] = x.completed; });
    setMarks(map);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setLoading(true);
    try {
      await api.post("/workouts/generate");
      await load();
      toast.success(lang === "id" ? "Jadwal dibuat" : "Plan generated");
    } finally {
      setLoading(false);
    }
  };

  const toggleMark = async (idx) => {
    const date = todayISO();
    const key = `${date}:${idx}`;
    const newVal = !marks[key];
    setMarks({ ...marks, [key]: newVal });
    try {
      await api.post("/workouts/mark", { date, exercise_index: idx, completed: newVal });
    } catch {
      setMarks({ ...marks, [key]: !newVal });
    }
  };

  const itemName = (it) => (lang === "id" ? it.name : it.name_en) || it.name;
  const itemSteps = (it) => (lang === "id" ? it.steps : it.steps_en) || it.steps || [];

  return (
    <div className="space-y-8" data-testid="workouts-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#4A5D23] font-bold">Gracely</p>
          <h1 className="font-serif-display text-4xl text-stone-900 mt-2">{t("workouts_title")}</h1>
          {plan && (
            <p className="text-stone-500 mt-1">
              {plan.is_low_impact ? (lang === "id" ? "Program low-impact yang aman untuk pemulihanmu." : "Low-impact program safe for your recovery.") : (lang === "id" ? "Program kebugaran seimbang untuk stamina." : "Balanced fitness program for stamina.")}
            </p>
          )}
        </div>
        <Button onClick={generate} disabled={loading} className="rounded-full bg-[#4A5D23] hover:bg-[#3A4A1C] text-white btn-pop" data-testid="regen-plan-btn">
          {loading ? "…" : (plan ? (lang === "id" ? "Buat ulang" : "Regenerate") : t("generate_plan"))}
        </Button>
      </div>

      {!plan && (
        <div className="card-soft p-10 text-center">
          <Dumbbell className="w-10 h-10 mx-auto text-stone-300" />
          <p className="mt-4 text-stone-600">{t("dash_no_workout")}</p>
        </div>
      )}

      {plan && (
        <div className="grid gap-4">
          {plan.days.map((d, idx) => {
            const dt = todayISO();
            const isToday = DAYS_ID[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1] === d.day;
            const done = !!marks[`${dt}:${idx}`];
            return (
              <div key={idx} className={`card-soft overflow-hidden ${isToday ? "ring-2 ring-[#4A5D23]" : ""}`} data-testid={`day-${idx}`}>
                {d.image && (
                  <div className="relative h-44 overflow-hidden bg-stone-100">
                    <img src={d.image} alt={d.title} className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-white">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">{lang === "id" ? d.day : d.day_en}</p>
                        <p className="font-serif-display text-2xl leading-tight">{lang === "id" ? d.title : d.title_en}</p>
                      </div>
                      {isToday && <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-white text-[#4A5D23]">{lang === "id" ? "Hari ini" : "Today"}</span>}
                    </div>
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${d.tag === "Recovery" ? "bg-[#EAF0E4] text-[#4A5D23]" : d.tag === "HIIT" ? "bg-[#FFEDE7] text-[#E07A5F]" : "bg-stone-100 text-stone-700"}`}>{d.tag}</span>
                      <p className="text-sm text-stone-500 flex items-center gap-2"><Zap className="w-3 h-3" /> {d.duration_min} min · {d.intensity}</p>
                    </div>
                    {isToday && (
                      <Button onClick={() => toggleMark(idx)} className={`rounded-full ${done ? "bg-[#4A5D23] hover:bg-[#3A4A1C]" : "bg-[#E07A5F] hover:bg-[#C96D55]"} text-white btn-pop`} data-testid={`mark-btn-${idx}`}>
                        <Check className="w-4 h-4 mr-1" /> {done ? t("marked_done") : t("mark_done")}
                      </Button>
                    )}
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {(d.items || []).map((it, i) => {
                      const isStructured = it && typeof it === "object" && it.name;
                      const label = isStructured ? itemName(it) : it;
                      const meta = isStructured ? it.meta : "";
                      const hasSteps = isStructured && (itemSteps(it).length > 0);
                      const style = categoryFor(it);
                      const Icon = style.Icon;
                      return (
                        <button
                          key={i}
                          onClick={() => hasSteps && setOpenItem({ ...it, dayIdx: idx, itemIdx: i })}
                          className={`text-left p-3 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors flex items-center gap-3 ${hasSteps ? "cursor-pointer" : "cursor-default"}`}
                          data-testid={`exercise-${idx}-${i}`}
                        >
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${style.bg} ${style.color}`}>
                            <Icon className="w-6 h-6" strokeWidth={2} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-stone-800 truncate">{label}</p>
                            {meta && <p className="text-xs text-stone-500 truncate">{meta}</p>}
                          </div>
                          {hasSteps && <ChevronRight className="w-4 h-4 text-stone-400" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!openItem} onOpenChange={(o) => !o && setOpenItem(null)}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="exercise-dialog">
          {openItem && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif-display text-2xl flex items-center gap-3">
                  {(() => {
                    const style = categoryFor(openItem);
                    const Icon = style.Icon;
                    return (
                      <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${style.bg} ${style.color}`}>
                        <Icon className="w-5 h-5" />
                      </span>
                    );
                  })()}
                  {itemName(openItem)}
                </DialogTitle>
              </DialogHeader>
              {openItem.image && (
                <div className={`w-full h-48 rounded-xl overflow-hidden ${categoryFor(openItem).ring} flex items-center justify-center`}>
                  <img
                    src={openItem.image}
                    alt={itemName(openItem)}
                    className="w-full h-full object-cover"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                </div>
              )}
              {openItem.meta && <p className="text-sm text-stone-500 mt-1">{openItem.meta}</p>}
              <div className="mt-2">
                <p className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-3">{lang === "id" ? "Cara melakukannya" : "How to do it"}</p>
                <ol className="space-y-3">
                  {itemSteps(openItem).map((s, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#EAF0E4] text-[#4A5D23] flex items-center justify-center text-xs font-bold">{i + 1}</span>
                      <p className="text-sm text-stone-700 leading-relaxed pt-0.5">{s}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
